import createClient, { type Middleware } from 'openapi-fetch';
import { apiUrl, API_BASE, credentialsFor } from './config';
import type { components, paths } from './schema';

export type Schemas = components['schemas'];

/**
 * API-Client für das Backend (typisiert aus docs/openapi.json des Backends).
 * Die API läuft auf eigener Adresse (VITE_API_URL, siehe config.ts).
 * Anmeldung:
 *  - Betrieb: Cloudflare Access setzt das Cookie CF_Authorization für die API-Adresse, der Browser schickt es mit.
 *  - Lokal/Skripte: persönlicher API-Token, im Browser gespeichert und als Bearer-Header gesendet.
 */

const TOKEN_KEY = 'ita.apiToken';

export const tokenStore = {
  get(): string | null {
    try {
      return localStorage.getItem(TOKEN_KEY);
    } catch {
      return null;
    }
  },
  set(token: string) {
    try {
      localStorage.setItem(TOKEN_KEY, token);
    } catch {
      /* ignorieren */
    }
  },
  clear() {
    try {
      localStorage.removeItem(TOKEN_KEY);
    } catch {
      /* ignorieren */
    }
  },
};

export class ApiError extends Error {
  constructor(
    readonly status: number,
    readonly code: string,
    message: string,
    readonly details?: unknown,
    readonly requestId?: string | null,
  ) {
    super(message);
    this.name = 'ApiError';
  }
}

const authMiddleware: Middleware = {
  onRequest({ request }) {
    const token = tokenStore.get();
    if (token && !request.headers.has('Authorization')) request.headers.set('Authorization', `Bearer ${token}`);
    return request;
  },
};

export const api = createClient<paths>({
  baseUrl: API_BASE,
  fetch: (request: Request) => fetch(request, { credentials: credentialsFor(!!tokenStore.get()) }),
});
api.use(authMiddleware);

type ErrorBody = { error?: { code?: string; message?: string; details?: unknown; request_id?: string | null } };

export const toApiError = (status: number, body: unknown): ApiError => {
  const e = (body as ErrorBody | undefined)?.error;
  return new ApiError(status, e?.code ?? `HTTP_${status}`, e?.message ?? `Anfrage fehlgeschlagen (${status})`, e?.details, e?.request_id);
};

/** Ergebnis von openapi-fetch auspacken: Daten oder ApiError werfen. */
export const unwrap = async <T>(promise: Promise<{ data?: T; error?: unknown; response: Response }>): Promise<T> => {
  const { data, error, response } = await promise;
  if (!response.ok || error !== undefined) throw toApiError(response.status, error);
  return data as T;
};

/** Rohe Anfrage (Downloads, Multipart-Uploads) mit Anmeldung. url = "/api/…" */
export const rawFetch = async (url: string, init: RequestInit = {}): Promise<Response> => {
  const headers = new Headers(init.headers);
  const token = tokenStore.get();
  if (token && !headers.has('Authorization')) headers.set('Authorization', `Bearer ${token}`);
  const res = await fetch(apiUrl(url), { ...init, headers, credentials: credentialsFor(!!token) });
  if (!res.ok) {
    let body: unknown = null;
    try {
      body = await res.json();
    } catch {
      /* kein JSON */
    }
    throw toApiError(res.status, body);
  }
  return res;
};

export const uploadForm = async <T>(url: string, form: FormData): Promise<T> => {
  const res = await rawFetch(url, { method: 'POST', body: form });
  return (await res.json()) as T;
};

/** Datei laden und im Browser öffnen bzw. herunterladen (funktioniert auch mit Token-Anmeldung). */
export const openFile = async (url: string, opts: { download?: string } = {}) => {
  const res = await rawFetch(url);
  const blob = await res.blob();
  const objectUrl = URL.createObjectURL(blob);
  if (opts.download) {
    const a = document.createElement('a');
    a.href = objectUrl;
    a.download = opts.download;
    document.body.appendChild(a);
    a.click();
    a.remove();
  } else {
    window.open(objectUrl, '_blank', 'noopener');
  }
  setTimeout(() => URL.revokeObjectURL(objectUrl), 60_000);
};

/** fetch() selbst ist gescheitert (API nicht erreichbar, CORS, Access-Weiterleitung zur Anmeldung). */
export const isNetworkError = (err: unknown): boolean => err instanceof TypeError;

/** Fehlertext für Anzeige (deutsch, soweit bekannt). */
export const errorText = (err: unknown): string => {
  if (err instanceof ApiError) return ERROR_MESSAGES[err.code] ?? err.message;
  if (isNetworkError(err)) return 'Keine Verbindung zur API (Netzwerk, Adresse oder Anmeldung prüfen).';
  if (err instanceof Error) return err.message;
  return 'Unbekannter Fehler';
};

const ERROR_MESSAGES: Record<string, string> = {
  UNAUTHENTICATED: 'Nicht angemeldet.',
  FORBIDDEN: 'Dafür fehlen dir die Rechte.',
  VALIDATION_FAILED: 'Bitte die markierten Eingaben prüfen.',
  DATABASE_NOT_MIGRATED: 'Die Datenbank ist nicht auf dem neuesten Stand. Im Backend „npm run db:migrate:local“ (lokal) bzw. „npm run db:migrate:remote“ (Cloudflare) ausführen.',
  INVENTORY_FORMAT_UNKNOWN: 'Der Aufbau der Datei wurde nicht erkannt. Erwartet wird das Inventarverzeichnis der Buchhaltung mit der Spalte „AHK-Datum“.',
  RECONCILIATION_NOT_FOUND: 'Abgleich nicht gefunden.',
  FISCAL_YEAR_UNKNOWN: 'Das Wirtschaftsjahr steht nicht im Titel der Datei (z. B. „… bis 31.12.2025“) – Werte können deshalb nicht übernommen werden. Ohne Häkchen klappt der Abgleich.',
  DISPOSAL_REVERT_BLOCKED: 'Die Verschrottung lässt sich nicht automatisch zurücknehmen – eine Komponente wurde seitdem verändert.',
  CONCURRENT_MODIFICATION: 'Daten wurden währenddessen geändert. Bitte noch einmal versuchen.',
  INVALID_SPEC_VALUE: 'Bitte die technischen Daten prüfen.',
  ASSET_NOT_FOUND: 'Asset nicht gefunden.',
  EMPLOYEE_NOT_FOUND: 'Mitarbeiter nicht gefunden.',
  LOCATION_NOT_FOUND: 'Standort nicht gefunden.',
  ASSET_TYPE_NOT_FOUND: 'Asset-Typ nicht gefunden.',
  DISPOSAL_NOT_FOUND: 'Verschrottungsvorgang nicht gefunden.',
  USER_NOT_FOUND: 'Benutzer nicht gefunden.',
  VERSION_CONFLICT: 'Das Asset wurde inzwischen von jemand anderem geändert. Bitte neu laden.',
  VERSION_REQUIRED: 'Version fehlt – bitte neu laden.',
  DUPLICATE_ASSET_NUMBER: 'Diese Inventarnummer gibt es bereits.',
  DUPLICATE_SERIAL_NUMBER: 'Diese Seriennummer ist beim selben Hersteller bereits erfasst.',
  DUPLICATE_SHORT_CODE: 'Dieses Kürzel gibt es bereits.',
  DUPLICATE_LOCATION_NAME: 'Diesen Standort gibt es bereits.',
  DUPLICATE_USER_EMAIL: 'Es gibt bereits einen Benutzer mit dieser E-Mail.',
  ASSET_ALREADY_DISPOSED: 'Das Asset wurde bereits verschrottet.',
  ASSET_DISPOSED_READ_ONLY: 'Verschrottete Assets können nicht mehr geändert werden.',
  ASSET_IS_INSTALLED: 'Das Asset ist in einem anderen Gerät eingebaut – bitte das Gerät zuordnen.',
  CHILD_ALREADY_INSTALLED: 'Die Komponente ist bereits in einem anderen Gerät eingebaut.',
  RELATIONSHIP_CYCLE: 'Das würde einen Kreisbezug erzeugen (Gerät in sich selbst).',
  MAX_DEPTH_EXCEEDED: 'Zu tiefe Verschachtelung.',
  INVALID_STATUS_TRANSITION: 'Dieser Statuswechsel ist nicht erlaubt.',
  EMPLOYEE_INACTIVE: 'Der Mitarbeiter ist deaktiviert.',
  LOCATION_INACTIVE: 'Der Standort ist deaktiviert.',
  EMPLOYEE_HAS_ASSETS: 'Dem Mitarbeiter sind noch Assets zugeordnet.',
  LOCATION_HAS_ASSETS: 'Am Standort befinden sich noch Assets.',
  CHILD_DECISION_REQUIRED: 'Für jede eingebaute Komponente muss entschieden werden, was mit ihr passiert.',
  FILE_TOO_LARGE: 'Die Datei ist zu groß.',
  UNSUPPORTED_MEDIA_TYPE: 'Dieser Dateityp ist nicht erlaubt.',
  FILE_CONTENT_MISMATCH: 'Der Dateiinhalt passt nicht zum Dateityp.',
  ATTACHMENT_PROTECTED: 'Verschrottungsprotokolle und Entsorgungsnachweise können nicht gelöscht werden.',
  ASSET_NOT_DELETABLE: 'Das Asset kann nicht gelöscht werden.',
  LAST_ADMIN: 'Es muss mindestens ein aktiver Administrator bleiben.',
  USER_NOT_REGISTERED: 'Für deine E-Mail-Adresse gibt es noch kein Benutzerkonto.',
  USER_INACTIVE: 'Dein Benutzerkonto ist deaktiviert.',
};

/** Feldfehler aus VALIDATION_FAILED/INVALID_SPEC_VALUE als { pfad: meldung }. */
export const fieldErrors = (err: unknown): Record<string, string> => {
  if (!(err instanceof ApiError) || !Array.isArray(err.details)) return {};
  const out: Record<string, string> = {};
  for (const d of err.details as Array<{ path?: string; message?: string }>) {
    if (d.path && d.message && !out[d.path]) out[d.path] = d.message;
  }
  return out;
};
